import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { Loading, Avatar } from "../components/common";
import { applyDart, resolveDart, checkoutSuggestion, rotate, START_501 } from "../lib/dartEngine";
import { gameLabel } from "./Dashboard";
import { toast } from "sonner";
import { Undo2, X, Home, Trophy, Delete, RotateCcw } from "lucide-react";
import MatchReplay from "../components/MatchReplay";
import Burst from "../components/Burst";
import { playCheer } from "../lib/fx";

const NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

function dartLabel(base, mult) {
  if (base === 0) return "MISS";
  if (base === 25) return mult === 2 ? "BULL" : "25";
  return (mult === 3 ? "T" : mult === 2 ? "D" : "") + base;
}

function computeSeries(legs, playerIds) {
  const s = {};
  playerIds.forEach((id) => (s[id] = 0));
  legs.forEach((l) => {
    if (l.winner_id in s) s[l.winner_id] += 1;
  });
  return s;
}

// Resilient match save: retries transient failures so a completed game is never lost silently.
async function saveMatchWithRetry(matchId, body, { attempts = 5, onSuccess, retryFn } = {}) {
  for (let i = 0; i < attempts; i++) {
    try {
      const { data } = await api.put(`/matches/${matchId}`, body);
      if (onSuccess) onSuccess(data);
      return true;
    } catch (e) {
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 700 * (i + 1)));
    }
  }
  toast.error("Couldn't save the match — check your connection and tap Retry.", {
    duration: 30000,
    action: retryFn ? { label: "Retry", onClick: retryFn } : undefined,
  });
  return false;
}

export default function LiveMatch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/matches/${id}`);
        setMatch(data);
      } catch {
        toast.error("Match not found");
        navigate("/");
      }
    })();
  }, [id, navigate]);

  if (!match) return <Loading label="Loading match" />;
  if (match.status === "completed")
    return <CompletedMatch match={match} navigate={navigate} />;
  if (match.status === "abandoned")
    return (
      <div className="text-center py-24">
        <p className="text-slate-400">This match was abandoned.</p>
        <button onClick={() => navigate("/")} className="mt-4 text-[#007AFF] font-bold">
          Return Home
        </button>
      </div>
    );

  if (match.game_type === "501")
    return <Live501 match={match} setMatch={setMatch} navigate={navigate} />;
  if (match.game_type === "baseball")
    return <LiveBaseball match={match} setMatch={setMatch} navigate={navigate} />;
  return <LiveATW match={match} setMatch={setMatch} navigate={navigate} />;
}

// ---------------- 501 ----------------
function Live501({ match, setMatch, navigate }) {
  const { player_ids, player_names, finish_rule, legs_needed, best_of } = match;
  const start_rule = match.start_rule || "straight_in";

  const newLeg = useCallback(
    (legNumber) => {
      const startingId = player_ids[(legNumber - 1) % player_ids.length];
      const remaining = {};
      const started = {};
      player_ids.forEach((pid) => {
        remaining[pid] = START_501;
        started[pid] = false;
      });
      return {
        legNumber,
        startingId,
        order: rotate(player_ids, startingId),
        turnPtr: 0,
        remaining,
        started,
        darts: [],
        turns: [],
      };
    },
    [player_ids]
  );

  const [completedLegs, setCompletedLegs] = useState(match.legs || []);
  const [leg, setLeg] = useState(() => newLeg((match.legs || []).length + 1));
  const [mult, setMult] = useState(1);
  const [phase, setPhase] = useState("playing"); // playing | leg_over
  const [lastLegWinner, setLastLegWinner] = useState(null);
  const [flash, setFlash] = useState(0);
  const [burst, setBurst] = useState(null);
  const [reopenLatch, setReopenLatch] = useState(false);

  const series = useMemo(() => computeSeries(completedLegs, player_ids), [completedLegs, player_ids]);
  const cp = leg.order[leg.turnPtr];
  const usedThisTurn = leg.darts.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
  const liveRemaining = leg.remaining[cp] - usedThisTurn;
  const cpStarted = !!leg.started[cp];
  const suggestion = cpStarted ? checkoutSuggestion(liveRemaining, finish_rule) : "";
  const needsDoubleIn = start_rule === "double_in" && !cpStarted;

  const playerAvg = (pid) => {
    let darts = 0,
      points = 0;
    const addTurn = (t) => {
      if (t.player_id !== pid) return;
      darts += t.darts_used || (t.darts ? t.darts.length : 0);
      points += t.busted ? 0 : t.turn_score || 0;
    };
    completedLegs.forEach((l) => (l.turns || []).forEach(addTurn));
    leg.turns.forEach(addTurn);
    if (pid === cp) {
      leg.darts.forEach((d) => {
        darts += 1;
        points += d.counted ? d.value : 0;
      });
    }
    return darts ? +((points / darts) * 3).toFixed(1) : 0;
  };

  // Most recently COMPLETED turn for a player (reads existing match state, no DB writes)
  const lastVisit = (pid) => {
    const all = [];
    completedLegs.forEach((l) => (l.turns || []).forEach((t) => all.push(t)));
    leg.turns.forEach((t) => all.push(t));
    for (let i = all.length - 1; i >= 0; i--) {
      if (all[i].player_id === pid) return all[i];
    }
    return null;
  };

  const visitDartLabel = (d) => {
    if (!d) return "—";
    if (d.base === 0) return "MISS";
    if (d.base === 25) return d.multiplier === 2 ? "BULL" : "25";
    return (d.multiplier === 3 ? "T" : d.multiplier === 2 ? "D" : "") + d.base;
  };

  const persist = (legs, status, winnerId) => {
    const body = { legs };
    if (status) body.status = status;
    if (winnerId) body.winner_id = winnerId;
    return saveMatchWithRetry(match.id, body, {
      attempts: status === "completed" ? 5 : 2,
      onSuccess: (data) => {
        if (status === "completed") setMatch(data);
      },
      retryFn: () => persist(legs, status, winnerId),
    });
  };

  const endTurn = (dartsThrown, busted, isCheckout, startedMap) => {
    setReopenLatch(false);
    const remBefore = leg.remaining[cp];
    const score = busted ? 0 : dartsThrown.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
    const remAfter = busted ? remBefore : remBefore - score;
    if (isCheckout) {
      setBurst({ label: "CHECKOUT", sub: `${score}`, color: "#FFCC00" });
      playCheer("checkout");
    } else if (!busted && score === 180) {
      setBurst({ label: "180", color: "#FF3B30" });
      playCheer("180");
    }
    const turn = {
      player_id: cp,
      darts: dartsThrown,
      turn_score: score,
      remaining_before: remBefore,
      remaining_after: remAfter,
      busted,
      is_checkout: isCheckout,
      darts_used: dartsThrown.length,
    };
    const turns = [...leg.turns, turn];

    if (isCheckout) {
      const finishedLeg = {
        leg_number: leg.legNumber,
        starting_player_id: leg.startingId,
        winner_id: cp,
        turns,
      };
      const allLegs = [...completedLegs, finishedLeg];
      const newSeries = computeSeries(allLegs, player_ids);
      setCompletedLegs(allLegs);
      if (newSeries[cp] >= legs_needed) {
        setLastLegWinner(cp);
        setTimeout(() => persist(allLegs, "completed", cp), 1500);
      } else {
        persist(allLegs);
        setLastLegWinner(cp);
        setPhase("leg_over");
      }
      return;
    }
    setMult(1);
    setLeg({
      ...leg,
      turns,
      started: startedMap,
      remaining: { ...leg.remaining, [cp]: remAfter },
      darts: [],
      turnPtr: (leg.turnPtr + 1) % leg.order.length,
    });
  };

  const handleDart = (base, forceMult) => {
    if (phase !== "playing") return;
    if (busted) return;
    const m = forceMult ? forceMult : base === 25 && mult === 3 ? 2 : mult;
    const dart = { base, multiplier: m, value: base * m, label: dartLabel(base, m) };
    const usedCounted = leg.darts.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
    const res = resolveDart(leg.remaining[cp], usedCounted, dart, finish_rule, !!leg.started[cp], start_rule);
    dart.counted = res.counted;
    const newDarts = [...leg.darts, dart];
    const startedMap = res.started ? { ...leg.started, [cp]: true } : leg.started;
    setFlash((f) => f + 1);
    setMult(1);
    if (res.status === "win") return endTurn(newDarts, false, true, startedMap);
    if (res.status === "bust") {
      setLeg({ ...leg, darts: newDarts, started: startedMap, busted: true });
      setTimeout(() => endTurn(newDarts, true, false, startedMap), 550);
      return;
    }
    // 'ok' or 'nostart'
    if (newDarts.length === 3) return endTurn(newDarts, false, false, startedMap);
    setLeg({ ...leg, darts: newDarts, started: startedMap });
  };

  const busted = leg.busted;

  const undo = () => {
    // Transient bust animation in progress — ignore until it commits.
    if (busted) return;

    // Mid-visit: remove only the current player's last dart (unchanged behaviour).
    if (leg.darts.length > 0) {
      setLeg({ ...leg, darts: leg.darts.slice(0, -1) });
      return;
    }

    // Idle pad (visit just completed): reopen the most recently completed visit in this
    // leg so a mistaken 3rd dart (or bust) can be corrected. Only the single most recent
    // visit is reversible, and only before the next player has thrown any dart.
    if (reopenLatch) return;
    if (leg.turns.length === 0) return;
    const lastTurn = leg.turns[leg.turns.length - 1];
    const prevPtr = (leg.turnPtr - 1 + leg.order.length) % leg.order.length;
    const prevPid = leg.order[prevPtr];
    if (lastTurn.player_id !== prevPid) return;

    const restoredDarts = lastTurn.darts.slice(0, -1);
    const startedForPrev =
      start_rule !== "double_in" ||
      restoredDarts.some((d) => d.counted) ||
      leg.turns.slice(0, -1).some((t) => t.player_id === prevPid && t.turn_score > 0);

    setMult(1);
    setReopenLatch(true);
    setLeg({
      ...leg,
      turns: leg.turns.slice(0, -1),
      darts: restoredDarts,
      remaining: { ...leg.remaining, [prevPid]: lastTurn.remaining_before },
      started: { ...leg.started, [prevPid]: startedForPrev },
      turnPtr: prevPtr,
      busted: false,
    });
  };

  const startNextLeg = () => {
    setLeg(newLeg(leg.legNumber + 1));
    setMult(1);
    setReopenLatch(false);
    setPhase("playing");
  };

  const abandon = async () => {
    if (!window.confirm("Abandon this match? It will NOT count toward records.")) return;
    await api.put(`/matches/${match.id}`, { status: "abandoned" });
    navigate("/");
  };

  const needMsg = () => {
    const leader = player_ids.reduce((a, b) => (series[a] >= series[b] ? a : b));
    const need = legs_needed - series[leader];
    if (series[leader] === 0) return null;
    return `${player_names[leader]} needs ${need} more leg${need > 1 ? "s" : ""} to win`;
  };

  return (
    <div className="max-w-5xl mx-auto">
      <TopBar match={match} legNumber={leg.legNumber} onAbandon={abandon} navigate={navigate} />

      {/* Match series */}
      <div className="panel rounded-sm p-4 mb-1">
        <div className="text-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
          Match — Best of {best_of}
        </div>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {player_ids.map((pid) => (
            <div key={pid} className="text-center" data-testid={`series-${pid}`}>
              <div className="text-sm font-bold uppercase text-slate-300">{player_names[pid]}</div>
              <div className="font-display text-5xl font-black text-white leading-none">
                {series[pid]}
              </div>
            </div>
          ))}
        </div>
        {needMsg() && (
          <div className="text-center text-[#FFCC00] text-sm font-bold uppercase tracking-wide mt-2">
            {needMsg()}
          </div>
        )}
      </div>

      {/* Current leg scoreboard */}
      <div className={`grid gap-1 mb-4`} style={{ gridTemplateColumns: `repeat(${player_ids.length}, minmax(0,1fr))` }}>
        {leg.order.map((pid) => {
          const active = pid === cp && phase === "playing";
          const rem = pid === cp ? liveRemaining : leg.remaining[pid];
          return (
            <div
              key={pid}
              data-testid={`leg-score-${pid}`}
              className={`panel rounded-sm p-4 text-center relative ${
                active ? "border-t-2 border-t-[#FF3B30] bg-[#161010]" : "opacity-70"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Avatar name={player_names[pid]} size={24} active={active} />
                <span className="text-sm font-bold uppercase text-slate-200 truncate">
                  {player_names[pid]}
                </span>
              </div>
              <motion.div
                key={`${pid}-${rem}-${flash}`}
                initial={active ? { backgroundColor: "rgba(255,255,255,0.12)" } : false}
                animate={{ backgroundColor: "rgba(0,0,0,0)" }}
                transition={{ duration: 0.4 }}
                className="font-display text-6xl md:text-7xl font-black text-white leading-none rounded-sm"
              >
                {rem}
              </motion.div>
              <div
                data-testid={`avg-${pid}`}
                className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mt-1"
              >
                Avg <span className="text-[#007AFF]">{playerAvg(pid)}</span>
              </div>
              {(() => {
                const lv = lastVisit(pid);
                return (
                  <div className="mt-2 pt-2 border-t border-[#1c1c1c]" data-testid={`last-visit-${pid}`}>
                    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-1">
                      Last Visit
                    </div>
                    <div className="flex items-center justify-center gap-1.5">
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          data-testid={`last-visit-dart-${pid}-${i}`}
                          className="min-w-[36px] px-1.5 py-0.5 rounded-sm bg-[#141414] border border-[#262626] font-display text-xs font-bold text-slate-300"
                        >
                          {lv ? visitDartLabel(lv.darts?.[i]) : "—"}
                        </span>
                      ))}
                      <span
                        data-testid={`last-visit-total-${pid}`}
                        className={`ml-1.5 font-display text-lg font-black leading-none ${
                          lv && lv.busted ? "text-[#FF3B30]" : "text-white"
                        }`}
                      >
                        {lv ? (lv.busted ? "BUST" : lv.turn_score) : 0}
                      </span>
                    </div>
                  </div>
                );
              })()}
              {active && suggestion && (
                <div className="text-[#FFCC00] text-xs font-bold uppercase tracking-wide mt-1">
                  {suggestion}
                </div>
              )}
              {active && needsDoubleIn && (
                <div className="text-[#FF9500] text-xs font-bold uppercase tracking-wide mt-1">
                  Hit a double to start
                </div>
              )}
            </div>
          );
        })}
      </div>

      {phase === "playing" && (
        <>
          {/* Current darts */}
          <div className="flex items-center justify-center gap-2 mb-3 h-9">
            {[0, 1, 2].map((i) => {
              const d = leg.darts[i];
              return (
                <div
                  key={i}
                  data-testid={`current-dart-${i}`}
                  className={`min-w-[64px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${
                    d
                      ? busted
                        ? "border-[#FF3B30] text-[#FF3B30]"
                        : "border-[#007AFF] text-white"
                      : "border-[#262626] text-slate-700"
                  }`}
                >
                  {d ? d.label : "—"}
                </div>
              );
            })}
            {busted && (
              <span className="font-display text-2xl font-black text-[#FF3B30] uppercase ml-2">
                Bust!
              </span>
            )}
          </div>

          {/* Number pad — grouped Single / Double / Treble cards (no mode switching) */}
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
            {NUMBERS.map((n) => (
              <div
                key={n}
                className="flex flex-col rounded-md overflow-hidden border border-[#262626] bg-[#141414]"
              >
                <button
                  data-testid={`dart-${n}`}
                  onClick={() => handleDart(n, 1)}
                  className="py-3.5 font-display text-2xl font-bold text-white hover:bg-[#1f1f1f] active:bg-[#007AFF] transition-colors duration-100"
                >
                  {n}
                </button>
                <div className="grid grid-cols-2 border-t border-[#262626]">
                  <button
                    data-testid={`dart-d-${n}`}
                    onClick={() => handleDart(n, 2)}
                    className="py-4 font-display text-lg font-bold text-[#4C9AFF] border-r border-[#262626] hover:bg-[#1f1f1f] active:bg-[#007AFF] active:text-white transition-colors duration-100"
                  >
                    D{n}
                  </button>
                  <button
                    data-testid={`dart-t-${n}`}
                    onClick={() => handleDart(n, 3)}
                    className="py-4 font-display text-lg font-bold text-[#4C9AFF] hover:bg-[#1f1f1f] active:bg-[#007AFF] active:text-white transition-colors duration-100"
                  >
                    T{n}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Special inputs + actions */}
          <div className="grid grid-cols-4 gap-2 mt-2">
            <button
              data-testid="dart-25"
              onClick={() => handleDart(25, 1)}
              className="panel rounded-md py-4 font-display text-lg font-bold text-[#4ADE80] hover:bg-[#1f1f1f] transition-colors duration-100"
            >
              25
            </button>
            <button
              data-testid="dart-50"
              onClick={() => handleDart(25, 2)}
              className="panel rounded-md py-4 font-display text-lg font-bold text-[#F87171] hover:bg-[#1f1f1f] transition-colors duration-100"
            >
              BULL 50
            </button>
            <button
              data-testid="dart-miss"
              onClick={() => handleDart(0)}
              className="panel rounded-md py-4 font-display text-lg font-bold text-slate-400 hover:bg-[#1f1f1f] transition-colors duration-100"
            >
              MISS / 0
            </button>
            <button
              data-testid="undo-dart-btn"
              onClick={undo}
              className="panel rounded-md py-4 font-display text-sm font-bold uppercase tracking-wide text-slate-300 hover:text-[#F87171] flex items-center justify-center gap-1.5 transition-colors duration-100"
            >
              <Undo2 className="w-4 h-4" /> Undo
            </button>
          </div>
        </>
      )}

      <AnimatePresence>
        {burst && (
          <Burst
            label={burst.label}
            sub={burst.sub}
            color={burst.color}
            onDone={() => setBurst(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "leg_over" && lastLegWinner && (
          <LegOverOverlay
            winnerName={player_names[lastLegWinner]}
            legNumber={leg.legNumber}
            series={series}
            player_ids={player_ids}
            player_names={player_names}
            onNext={startNextLeg}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function LegOverOverlay({ winnerName, legNumber, series, player_ids, player_names, onNext, word = "Leg" }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-xl p-6"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="panel rounded-sm p-10 text-center max-w-md w-full border-t-4 border-t-[#FFCC00]"
      >
        <Trophy className="w-12 h-12 text-[#FFCC00] mx-auto mb-3" />
        <h2 className="font-display text-4xl font-black uppercase text-white leading-none">
          {winnerName} wins {word.toLowerCase()} {legNumber}
        </h2>
        <div className="flex items-center justify-center gap-6 my-6">
          {player_ids.map((pid) => (
            <div key={pid} className="text-center">
              <div className="text-xs font-bold uppercase text-slate-400">{player_names[pid]}</div>
              <div className="font-display text-5xl font-black text-white">{series[pid]}</div>
            </div>
          ))}
        </div>
        <button
          data-testid="start-next-leg-btn"
          onClick={onNext}
          className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white font-display text-xl font-black uppercase tracking-wider py-4 rounded-sm transition-colors duration-150"
        >
          Start Next Leg
        </button>
      </motion.div>
    </motion.div>
  );
}

// ---------------- Around the World (dart-by-dart) ----------------
const ATW_TARGETS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, "BULL"];

function LiveATW({ match, setMatch, navigate }) {
  const { player_ids, player_names, legs_needed, best_of } = match;

  const newRound = useCallback(
    (roundNumber) => {
      const startingId = player_ids[(roundNumber - 1) % player_ids.length];
      const progress = {};
      player_ids.forEach((pid) => (progress[pid] = 0));
      return {
        roundNumber,
        startingId,
        order: rotate(player_ids, startingId),
        turnPtr: 0,
        progress,
        darts: [],
        turns: [],
        startedAt: Date.now(),
      };
    },
    [player_ids]
  );

  const [completedLegs, setCompletedLegs] = useState(match.legs || []);
  const [round, setRound] = useState(() => newRound((match.legs || []).length + 1));
  const [phase, setPhase] = useState("playing");
  const [lastWinner, setLastWinner] = useState(null);

  const series = useMemo(() => computeSeries(completedLegs, player_ids), [completedLegs, player_ids]);
  const cp = round.order[round.turnPtr];

  const persist = (legs, status, winnerId) => {
    const body = { legs };
    if (status) body.status = status;
    if (winnerId) body.winner_id = winnerId;
    return saveMatchWithRetry(match.id, body, {
      attempts: status === "completed" ? 5 : 2,
      onSuccess: (data) => {
        if (status === "completed") setMatch(data);
      },
      retryFn: () => persist(legs, status, winnerId),
    });
  };

  const countHits = (darts) => darts.filter((d) => d.hit).length;

  const endTurn = (dartsThrown, progressVal) => {
    const turn = {
      player_id: cp,
      darts: dartsThrown,
      hits: countHits(dartsThrown),
      darts_used: dartsThrown.length,
    };
    setRound({
      ...round,
      turns: [...round.turns, turn],
      progress: { ...round.progress, [cp]: progressVal },
      darts: [],
      turnPtr: (round.turnPtr + 1) % round.order.length,
    });
  };

  const finishRound = (dartsThrown) => {
    const turn = {
      player_id: cp,
      darts: dartsThrown,
      hits: countHits(dartsThrown),
      darts_used: dartsThrown.length,
    };
    const leg = {
      leg_number: round.roundNumber,
      starting_player_id: round.startingId,
      winner_id: cp,
      turns: [...round.turns, turn],
      duration_ms: Date.now() - round.startedAt,
      atw: true,
    };
    const allLegs = [...completedLegs, leg];
    setCompletedLegs(allLegs);
    const newSeries = computeSeries(allLegs, player_ids);
    setLastWinner(cp);
    if (newSeries[cp] >= legs_needed) {
      persist(allLegs, "completed", cp);
    } else {
      persist(allLegs);
      setPhase("round_over");
    }
  };

  const throwDart = (mult) => {
    if (phase !== "playing") return;
    const targetIdx = round.progress[cp];
    const hit = mult > 0;
    const dart = { target: ATW_TARGETS[targetIdx], hit, mult };
    const newDarts = [...round.darts, dart];
    const newProgress = hit ? targetIdx + mult : targetIdx;
    if (hit && newProgress >= ATW_TARGETS.length) {
      finishRound(newDarts);
      return;
    }
    if (newDarts.length === 3) {
      endTurn(newDarts, newProgress);
      return;
    }
    setRound({ ...round, darts: newDarts, progress: { ...round.progress, [cp]: newProgress } });
  };

  const undo = () => {
    if (round.darts.length === 0) return;
    const last = round.darts[round.darts.length - 1];
    const newProgress = last.hit ? round.progress[cp] - (last.mult || 1) : round.progress[cp];
    setRound({
      ...round,
      darts: round.darts.slice(0, -1),
      progress: { ...round.progress, [cp]: newProgress },
    });
  };

  const startNextRound = () => {
    setRound(newRound(round.roundNumber + 1));
    setPhase("playing");
  };

  const abandon = async () => {
    if (!window.confirm("Abandon this match? It will NOT count toward records.")) return;
    await api.put(`/matches/${match.id}`, { status: "abandoned" });
    navigate("/");
  };

  const targetLabel = (idx) => (idx >= ATW_TARGETS.length ? "DONE" : `${ATW_TARGETS[idx]}`);

  return (
    <div className="max-w-4xl mx-auto">
      <TopBar match={match} legNumber={round.roundNumber} onAbandon={abandon} navigate={navigate} roundWord="Round" />

      <div className="panel rounded-sm p-4 mb-1">
        <div className="text-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
          Match — Best of {best_of}
        </div>
        <div className="flex items-center justify-center gap-6 flex-wrap">
          {player_ids.map((pid) => (
            <div key={pid} className="text-center" data-testid={`series-${pid}`}>
              <div className="text-sm font-bold uppercase text-slate-300">{player_names[pid]}</div>
              <div className="font-display text-5xl font-black text-white leading-none">
                {series[pid]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Player targets */}
      <div className="grid gap-1 mb-4" style={{ gridTemplateColumns: `repeat(${player_ids.length}, minmax(0,1fr))` }}>
        {round.order.map((pid) => {
          const active = pid === cp && phase === "playing";
          const idx = round.progress[pid];
          const pct = Math.round((idx / ATW_TARGETS.length) * 100);
          return (
            <div
              key={pid}
              data-testid={`atw-progress-${pid}`}
              className={`panel rounded-sm p-4 text-center ${
                active ? "border-t-2 border-t-[#FF3B30] bg-[#161010]" : "opacity-70"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Avatar name={player_names[pid]} size={24} active={active} />
                <span className="text-sm font-bold uppercase text-slate-200 truncate">
                  {player_names[pid]}
                </span>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                On target
              </div>
              <div className="font-display text-5xl md:text-6xl font-black text-white leading-none">
                {targetLabel(idx)}
              </div>
              <div className="mt-2 h-1.5 bg-[#262626] rounded-full overflow-hidden">
                <div className="h-full bg-[#34C759]" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] font-bold text-slate-500 mt-1">
                {idx}/{ATW_TARGETS.length}
              </div>
            </div>
          );
        })}
      </div>

      {phase === "playing" && (
        <>
          <div className="text-center mb-3">
            <div className="text-xs font-bold uppercase tracking-wide text-[#FFCC00]">
              {player_names[cp]} throwing at{" "}
              <span className="font-display text-lg">{targetLabel(round.progress[cp])}</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-4 h-9">
            {[0, 1, 2].map((i) => {
              const d = round.darts[i];
              return (
                <div
                  key={i}
                  className={`min-w-[72px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${
                    d
                      ? d.hit
                        ? "border-[#34C759] text-[#34C759]"
                        : "border-[#FF3B30] text-[#FF3B30]"
                      : "border-[#262626] text-slate-700"
                  }`}
                >
                  {d ? `${d.target}${d.hit && d.mult > 1 ? `×${d.mult}` : ""}${d.hit ? " ✓" : " ✗"}` : "—"}
                </div>
              );
            })}
          </div>

          <div className="max-w-lg mx-auto space-y-1">
            <div className="grid grid-cols-3 gap-1">
              <button
                data-testid="atw-single-btn"
                onClick={() => throwDart(1)}
                className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#34C759] hover:bg-[#2fb350] text-black transition-colors duration-100"
              >
                Single
              </button>
              <button
                data-testid="atw-double-btn"
                onClick={() => throwDart(2)}
                className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#007AFF] hover:bg-[#0069d9] text-white transition-colors duration-100"
              >
                Double
              </button>
              <button
                data-testid="atw-treble-btn"
                onClick={() => throwDart(3)}
                className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#FF3B30] hover:bg-[#e0332a] text-white transition-colors duration-100"
              >
                Treble
              </button>
            </div>
            <button
              data-testid="atw-miss-btn"
              onClick={() => throwDart(0)}
              className="w-full py-4 rounded-sm font-display text-2xl font-black uppercase tracking-wider panel text-slate-300 hover:bg-[#1f1f1f] transition-colors duration-100"
            >
              Miss
            </button>
            <button
              data-testid="atw-undo-btn"
              onClick={undo}
              className="w-full py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide panel text-slate-300 hover:text-[#FF3B30] flex items-center justify-center gap-1"
            >
              <Undo2 className="w-4 h-4" /> Undo Dart
            </button>
          </div>
        </>
      )}

      <AnimatePresence>
        {phase === "round_over" && lastWinner && (
          <LegOverOverlay
            winnerName={player_names[lastWinner]}
            legNumber={round.roundNumber}
            series={series}
            player_ids={player_ids}
            player_names={player_names}
            onNext={startNextRound}
            word="Round"
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function TopBar({ match, legNumber, onAbandon, navigate, roundWord = "Leg" }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30]">
          {gameLabel(match.game_type)}
          {match.game_type === "501" &&
            ` • ${match.start_rule === "double_in" ? "Double In" : "Straight In"} / ${
              match.finish_rule === "double_out" ? "Double Out" : "Straight Out"
            }`}
        </div>
        <div className="font-display text-3xl font-black uppercase text-white leading-none">
          {roundWord} {legNumber}
        </div>
      </div>
      <button
        data-testid="abandon-match-btn"
        onClick={onAbandon}
        className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-500 hover:text-[#FF3B30] transition-colors"
      >
        <X className="w-4 h-4" /> Abandon
      </button>
    </div>
  );
}

// ---------------- Victory ----------------
function VictoryScreen({ match, navigate }) {
  const { player_ids, player_names, winner_id, final_score, best_of, game_type } = match;
  const ps = match.player_stats || {};

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: -30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="text-center py-8"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FFCC00] text-black font-bold uppercase text-xs tracking-widest rounded-sm mb-4">
          <Trophy className="w-4 h-4" /> Champion
        </div>
        <h1
          data-testid="victory-winner"
          className="font-display text-7xl md:text-8xl font-black uppercase tracking-tighter text-white leading-none"
        >
          {player_names[winner_id]} Wins
        </h1>
        <div className="flex items-center justify-center gap-6 mt-6">
          {player_ids.map((pid, i) => (
            <div key={pid} className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-sm font-bold uppercase text-slate-400">{player_names[pid]}</div>
                <div
                  className={`font-display text-7xl font-black leading-none ${
                    pid === winner_id ? "text-[#FFCC00]" : "text-slate-500"
                  }`}
                >
                  {final_score[pid]}
                </div>
              </div>
              {i < player_ids.length - 1 && (
                <span className="font-display text-4xl text-slate-700">—</span>
              )}
            </div>
          ))}
        </div>
        <div className="text-slate-400 uppercase text-sm font-bold tracking-widest mt-4">
          {game_type === "baseball"
            ? `${(match.legs || []).length} Innings • Baseball`
            : `Best of ${best_of} • ${gameLabel(game_type)}`}
        </div>
      </motion.div>

      <div className="grid gap-1 sm:grid-cols-2 mt-4">
        {player_ids.map((pid, idx) => (
          <motion.div
            key={pid}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + idx * 0.1 }}
            className={`panel rounded-sm p-5 ${pid === winner_id ? "border-t-2 border-t-[#FFCC00]" : ""}`}
          >
            <div className="font-display text-2xl font-bold text-white mb-3">{player_names[pid]}</div>
            {game_type === "501" ? (
              <StatRows s={ps[pid] || {}} />
            ) : game_type === "baseball" ? (
              <BaseballStatRows s={ps[pid] || {}} />
            ) : (
              <AtwStatRows s={ps[pid] || {}} />
            )}
          </motion.div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button
          data-testid="rematch-btn"
          onClick={async () => {
            try {
              const { data } = await api.post("/matches", {
                game_type,
                finish_rule: match.finish_rule,
                start_rule: match.start_rule || "straight_in",
                best_of,
                player_ids,
              });
              navigate(`/match/${data.id}`);
            } catch {
              toast.error("Could not start rematch");
            }
          }}
          className="flex-1 flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5148] text-white font-bold uppercase tracking-wide py-4 rounded-sm transition-colors duration-150"
        >
          <RotateCcw className="w-5 h-5" /> Rematch
        </button>
        <button
          data-testid="view-full-stats-btn"
          onClick={() => navigate(`/players/${winner_id}`)}
          className="flex-1 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors duration-150"
        >
          View Winner Profile
        </button>
        <button
          data-testid="return-home-btn"
          onClick={() => navigate("/")}
          className="flex-1 flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase tracking-wide py-4 rounded-sm transition-colors duration-150"
        >
          <Home className="w-5 h-5" /> Return Home
        </button>
      </div>
    </div>
  );
}

function CompletedMatch({ match, navigate }) {
  return (
    <>
      <VictoryScreen match={match} navigate={navigate} />
      <div className="max-w-3xl mx-auto mt-10">
        {match.game_type === "baseball" ? (
          <div data-testid="baseball-completed-scoreboard">
            <h2 className="font-display text-3xl font-black uppercase text-white mb-3">Box Score</h2>
            <BaseballScoreboard
              match={match}
              innings={match.legs || []}
              totals={match.final_score || {}}
            />
          </div>
        ) : (
          <MatchReplay match={match} />
        )}
      </div>
    </>
  );
}

function BaseballStatRows({ s }) {
  const rows = [
    ["Total Runs", s.total_runs],
    ["Innings", s.innings],
    ["Runs / Inning", s.avg_runs_per_inning],
    ["Best Inning Total", s.best_score],
  ];
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1">
          <span className="text-slate-400 font-medium">{k}</span>
          <span className="font-display text-lg font-bold text-white">{v ?? 0}</span>
        </div>
      ))}
    </div>
  );
}

function AtwStatRows({ s }) {
  const rows = [
    ["Hit %", s.hit_pct != null ? `${s.hit_pct}%` : "—"],
    ["Darts Thrown", s.total_darts],
    ["Targets Hit", s.total_hits],
    ["Darts / Target", s.avg_darts_per_target],
    ["Fastest Round", s.fastest_round_ms ? `${(s.fastest_round_ms / 1000).toFixed(1)}s` : "—"],
  ];
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1">
          <span className="text-slate-400 font-medium">{k}</span>
          <span className="font-display text-lg font-bold text-white">{v ?? 0}</span>
        </div>
      ))}
    </div>
  );
}

function StatRows({ s }) {
  const rows = [
    ["Match Average", s.three_dart_avg],
    ["First 9 Avg", s.first9_avg],
    ["Highest Score", s.highest_turn],
    ["100+", s.count_100],
    ["140+", s.count_140],
    ["180s", s.count_180],
    ["Highest Checkout", s.highest_checkout],
    ["Checkout %", s.checkout_pct != null ? `${s.checkout_pct}%` : "—"],
  ];
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm border-b border-[#1c1c1c] py-1">
          <span className="text-slate-400 font-medium">{k}</span>
          <span className="font-display text-lg font-bold text-white">{v ?? 0}</span>
        </div>
      ))}
    </div>
  );
}


// ---------------- Baseball ----------------
function LiveBaseball({ match, setMatch, navigate }) {
  const { player_ids, player_names } = match;
  const bestOf = match.best_of || 1;
  const gamesNeeded = Math.floor(bestOf / 2) + 1;
  const [completedInnings, setCompletedInnings] = useState(match.legs || []);
  const [turns, setTurns] = useState([]); // committed turns for current inning
  const [darts, setDarts] = useState([]); // current thrower's darts {mult}
  const [phase, setPhase] = useState("playing"); // playing | inning_over | game_over | done
  const [burst, setBurst] = useState(null);

  const finishedGames = completedInnings.filter((i) => i.game_over);
  const seriesScore = {};
  player_ids.forEach((pid) => (seriesScore[pid] = 0));
  finishedGames.forEach((i) => {
    if (i.game_winner in seriesScore) seriesScore[i.game_winner] += 1;
  });
  const gameNumber = finishedGames.length + 1;
  const gameInnings = completedInnings.filter((i) => (i.game_number || 1) === gameNumber);
  const inningNumber = gameInnings.length + 1;

  const cpIdx = turns.length;
  const cp = player_ids[cpIdx];
  const target = inningNumber;
  const runsThisTurn = darts.reduce((s, d) => s + d.mult, 0);

  const runsFor = (pid) => {
    let s = 0;
    gameInnings.forEach((i) =>
      (i.turns || []).forEach((t) => {
        if (t.player_id === pid) s += t.runs;
      })
    );
    turns.forEach((t) => {
      if (t.player_id === pid) s += t.runs;
    });
    if (pid === cp) s += runsThisTurn;
    return s;
  };

  const persist = (innings, extra = {}) => {
    const body = { legs: innings, ...extra };
    return saveMatchWithRetry(match.id, body, {
      attempts: extra.status === "completed" ? 5 : 2,
      onSuccess: (data) => {
        if (extra.status === "completed") setMatch(data);
      },
      retryFn: () => persist(innings, extra),
    });
  };

  const finalizeInning = (allTurns) => {
    const inning = {
      leg_number: inningNumber,
      inning: inningNumber,
      target,
      game_number: gameNumber,
      turns: allTurns,
    };
    const totals = {};
    player_ids.forEach((pid) => (totals[pid] = 0));
    [...gameInnings, inning].forEach((i) =>
      (i.turns || []).forEach((t) => {
        totals[t.player_id] += t.runs;
      })
    );
    const maxRuns = Math.max(...player_ids.map((pid) => totals[pid]));
    const leaders = player_ids.filter((pid) => totals[pid] === maxRuns);
    const gameDecided = inningNumber >= 9 && leaders.length === 1;
    setTurns([]);
    setDarts([]);
    if (gameDecided) {
      const gw = leaders[0];
      inning.game_over = true;
      inning.game_winner = gw;
      const newCompleted = [...completedInnings, inning];
      setCompletedInnings(newCompleted);
      const newSeries = { ...seriesScore, [gw]: seriesScore[gw] + 1 };
      if (newSeries[gw] >= gamesNeeded) {
        setPhase("done");
        setBurst({ label: `${player_names[gw]} Wins`, sub: bestOf > 1 ? "SERIES OVER" : "GAME OVER", color: "#FFCC00" });
        playCheer("checkout");
        persist(newCompleted, { status: "completed", winner_id: gw, final_score: newSeries });
      } else {
        persist(newCompleted);
        setBurst({
          label: `${player_names[gw]} wins game ${gameNumber}`,
          sub: `Series ${player_ids.map((p) => newSeries[p]).join("–")}`,
          color: "#34C759",
        });
        setPhase("game_over");
      }
    } else {
      const newCompleted = [...completedInnings, inning];
      setCompletedInnings(newCompleted);
      persist(newCompleted);
      const extra = inningNumber >= 9;
      setBurst({
        label: extra ? "EXTRA INNINGS" : `Inning ${inningNumber} done`,
        sub: extra ? "Still tied!" : "",
        color: extra ? "#FF3B30" : "#34C759",
      });
      setPhase("inning_over");
    }
  };

  const throwDart = (mult) => {
    if (phase !== "playing") return;
    const newDarts = [...darts, { mult }];
    if (newDarts.length === 3) {
      const turn = {
        player_id: cp,
        darts: newDarts,
        runs: newDarts.reduce((s, d) => s + d.mult, 0),
        inning: inningNumber,
      };
      const newTurns = [...turns, turn];
      if (newTurns.length === player_ids.length) {
        finalizeInning(newTurns);
      } else {
        setTurns(newTurns);
        setDarts([]);
      }
      return;
    }
    setDarts(newDarts);
  };

  const undo = () => {
    if (phase !== "playing") return;
    if (darts.length > 0) {
      setDarts(darts.slice(0, -1));
    } else if (turns.length > 0) {
      const last = turns[turns.length - 1];
      setTurns(turns.slice(0, -1));
      setDarts(last.darts);
    }
  };

  const nextInning = () => setPhase("playing");

  const abandon = async () => {
    if (!window.confirm("Abandon this match? It won't count.")) return;
    await api.put(`/matches/${match.id}`, { status: "abandoned" });
    navigate("/");
  };

  const runsByPlayer = {};
  player_ids.forEach((pid) => {
    let r = null;
    const t = turns.find((x) => x.player_id === pid);
    if (t) r = t.runs;
    if (pid === cp) r = runsThisTurn;
    runsByPlayer[pid] = r;
  });

  return (
    <div className="max-w-3xl mx-auto">
      <TopBar match={match} legNumber={inningNumber} onAbandon={abandon} navigate={navigate} roundWord="Inning" />

      <div className="panel rounded-sm p-6 mb-4 text-center border-t-2 border-t-[#FF3B30]">
        {bestOf > 1 && (
          <div data-testid="baseball-series-score" className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FFCC00] mb-1">
            Game {gameNumber} · Best of {bestOf} · Series {player_ids.map((p) => seriesScore[p]).join("–")}
          </div>
        )}
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Inning {inningNumber} · Target
        </div>
        <div data-testid="baseball-target" className="font-display text-8xl font-black text-white leading-none">
          {target}
        </div>
        <div className="flex items-center justify-center gap-2 mt-3">
          <Avatar name={player_names[cp]} size={28} active />
          <span data-testid="baseball-current-player" className="text-sm font-bold uppercase text-[#FFCC00]">
            {player_names[cp]}'s turn
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-11">
        {[0, 1, 2].map((i) => {
          const d = darts[i];
          const lbl = d ? (d.mult === 0 ? "MISS" : d.mult === 1 ? "1" : d.mult === 2 ? "2" : "3") : "—";
          return (
            <div
              key={i}
              className={`min-w-[64px] px-3 py-2 rounded-sm text-center font-display text-lg font-bold border ${
                d
                  ? d.mult > 0
                    ? "border-[#34C759] text-[#34C759]"
                    : "border-[#FF3B30] text-[#FF3B30]"
                  : "border-[#262626] text-slate-700"
              }`}
            >
              {lbl}
            </div>
          );
        })}
        <div className="ml-2 text-center">
          <div className="text-[10px] font-bold uppercase text-slate-500">Runs</div>
          <div className="font-display text-2xl font-black text-[#FFCC00]">{runsThisTurn}</div>
        </div>
      </div>

      <div className="max-w-lg mx-auto space-y-1 mb-6">
        <div className="grid grid-cols-3 gap-1">
          <button
            data-testid="bb-single-btn"
            onClick={() => throwDart(1)}
            className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#34C759] hover:bg-[#2fb350] text-black transition-colors duration-100"
          >
            Single<span className="block text-xs font-bold opacity-70">+1</span>
          </button>
          <button
            data-testid="bb-double-btn"
            onClick={() => throwDart(2)}
            className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#007AFF] hover:bg-[#0069d9] text-white transition-colors duration-100"
          >
            Double<span className="block text-xs font-bold opacity-70">+2</span>
          </button>
          <button
            data-testid="bb-triple-btn"
            onClick={() => throwDart(3)}
            className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#FF3B30] hover:bg-[#e0332a] text-white transition-colors duration-100"
          >
            Triple<span className="block text-xs font-bold opacity-70">+3</span>
          </button>
        </div>
        <button
          data-testid="bb-miss-btn"
          onClick={() => throwDart(0)}
          className="w-full py-4 rounded-sm font-display text-2xl font-black uppercase tracking-wider panel text-slate-300 hover:bg-[#1f1f1f] transition-colors duration-100"
        >
          Miss <span className="text-sm opacity-70">+0</span>
        </button>
        <button
          data-testid="bb-undo-btn"
          onClick={undo}
          className="w-full py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide panel text-slate-300 hover:text-[#FF3B30] flex items-center justify-center gap-1"
        >
          <Undo2 className="w-4 h-4" /> Undo Dart
        </button>
      </div>

      <BaseballScoreboard
        match={match}
        innings={gameInnings}
        live={phase === "playing" ? { number: inningNumber, runsByPlayer, activePid: cp } : null}
      />

      <AnimatePresence>
        {phase === "inning_over" && (
          <BaseballInningOverlay
            match={match}
            runsFor={runsFor}
            inningNumber={inningNumber}
            extra={inningNumber >= 9}
            onNext={nextInning}
          />
        )}
        {phase === "game_over" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6"
          >
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="panel rounded-sm p-8 max-w-sm w-full text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-[#34C759]">Game {gameNumber - 1} complete</div>
              <div className="my-4 font-display text-2xl font-black uppercase text-white">
                Series {player_ids.map((p) => seriesScore[p]).join(" – ")}
              </div>
              <div className="space-y-2 mb-5">
                {player_ids.map((pid) => (
                  <div key={pid} className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Avatar name={player_names[pid]} size={28} /><span className="font-bold text-white">{player_names[pid]}</span></div>
                    <span className="font-display text-2xl font-black text-[#FFCC00]">{seriesScore[pid]}</span>
                  </div>
                ))}
              </div>
              <button
                data-testid="baseball-next-game-btn"
                onClick={() => setPhase("playing")}
                className="w-full bg-[#FF3B30] hover:bg-[#ff5148] text-white font-display text-xl font-black uppercase tracking-wider py-4 rounded-sm transition-colors"
              >
                Game {gameNumber} →
              </button>
            </motion.div>
          </motion.div>
        )}
        {burst && phase !== "inning_over" && phase !== "game_over" && (
          <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function BaseballInningOverlay({ match, runsFor, inningNumber, extra, onNext }) {
  const { player_ids, player_names } = match;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="panel rounded-sm p-8 max-w-sm w-full text-center"
      >
        <div className={`text-xs font-bold uppercase tracking-widest ${extra ? "text-[#FF3B30]" : "text-[#34C759]"}`}>
          {extra ? "Still tied — Extra innings!" : `Inning ${inningNumber} complete`}
        </div>
        <div className="my-5 space-y-2">
          {player_ids.map((pid) => (
            <div key={pid} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar name={player_names[pid]} size={28} />
                <span className="font-bold text-white">{player_names[pid]}</span>
              </div>
              <span className="font-display text-3xl font-black text-[#FFCC00]">{runsFor(pid)}</span>
            </div>
          ))}
        </div>
        <button
          data-testid="baseball-next-inning-btn"
          onClick={onNext}
          className="w-full bg-[#FF3B30] hover:bg-[#ff5148] text-white font-display text-xl font-black uppercase tracking-wider py-4 rounded-sm transition-colors"
        >
          Inning {inningNumber + 1} →
        </button>
      </motion.div>
    </motion.div>
  );
}

function BaseballScoreboard({ match, innings, live, totals }) {
  const { player_ids, player_names } = match;
  const cols = innings.map((i) => i.inning || i.leg_number);
  if (live) cols.push(live.number);

  const cellRun = (pid, inning) => {
    const t = (inning.turns || []).find((x) => x.player_id === pid);
    return t ? t.runs : null;
  };
  const totalFor = (pid) => {
    if (totals && totals[pid] != null) return totals[pid];
    let s = 0;
    innings.forEach((i) => {
      const t = (i.turns || []).find((x) => x.player_id === pid);
      if (t) s += t.runs;
    });
    if (live && live.runsByPlayer[pid] != null) s += live.runsByPlayer[pid];
    return s;
  };

  return (
    <div className="panel rounded-sm overflow-x-auto" data-testid="baseball-scoreboard">
      <table className="w-full text-sm min-w-[420px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-[#262626]">
            <th className="text-left p-2 sticky left-0 bg-[#141414] z-10">Player</th>
            {cols.map((c, idx) => (
              <th
                key={idx}
                className={`p-2 text-center w-9 ${live && idx === cols.length - 1 ? "text-[#FFCC00]" : ""}`}
              >
                {c}
              </th>
            ))}
            <th className="p-2 text-center text-white">Tot</th>
          </tr>
        </thead>
        <tbody>
          {player_ids.map((pid) => (
            <tr key={pid} className="border-b border-[#1a1a1a] last:border-0">
              <td className="p-2 font-bold text-white sticky left-0 bg-[#141414] z-10 whitespace-nowrap">
                {player_names[pid]}
              </td>
              {innings.map((inning, idx) => {
                const r = cellRun(pid, inning);
                return (
                  <td key={idx} className="p-2 text-center text-slate-300 font-semibold">
                    {r == null ? "·" : r}
                  </td>
                );
              })}
              {live && (
                <td
                  className={`p-2 text-center font-semibold ${
                    live.activePid === pid ? "text-[#FFCC00]" : "text-slate-500"
                  }`}
                >
                  {live.runsByPlayer[pid] == null ? "·" : live.runsByPlayer[pid]}
                </td>
              )}
              <td className="p-2 text-center font-display text-lg font-black text-[#FFCC00]">
                {totalFor(pid)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
